/**
 * Save and Save anyway of the connection dialog (moved out of `NewConnectionPopup`, C0).
 *
 * Round B: a save is authorized for the draft revision captured when it was pressed — before its first await — and
 * re-checked after the test, before `namedSharesForSave` and right before `onSave`; it persists the values on screen or
 * refuses and says so (silently once the dialog was dismissed). C0: both run the shared validation gate first (Save
 * before `form.handleSubmit()`), count as "in flight" until they settle (the entry points say "Saving…"), and a save
 * that keeps the dialog open clears every engine draft.
 */
import { useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Connection, SaveConnectionOptions } from '@/types/electron';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import { namedSharesForSave } from '../../components/namedSharesSave';
import { stripTransientSshSecrets } from '../../utils/sshTransientSecrets';
import type { useConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import type { useDraftRevision } from '../../hooks/useDraftRevision';
import { buildConnection, normalizeValues, saveOptionsFor } from '../state/connectionRecord';
import type { ConnectionForm } from '../state/connectionForm';
import type { useSaveInFlight } from '../state/saveAuthorization';
import type { ConnectionTestRun, TestSource } from './useConnectionTestRun';
import type { SaveOrigin } from '../state/dialogView';
import { FUNNEL_VERSION } from './useConnectionDialogFunnel';

/** Round B R2: a Save whose test answered for values the user has since edited saves nothing. */
export const CHANGED_WHILE_TESTING = 'The connection details changed while they were being tested, so nothing was saved. Test or save again.';

type Options = {
  form: ConnectionForm;
  tests: ReturnType<typeof useConnectionTestOutcome>;
  draft: ReturnType<typeof useDraftRevision>;
  saving: ReturnType<typeof useSaveInFlight>;
  runGate: () => Promise<boolean>;
  runTest: (values: ConnectionFormValues, skipValidation: boolean, source: TestSource) => Promise<ConnectionTestRun>;
  onSaved: () => void;
  onSave: (connection: Connection, options: SaveConnectionOptions) => Promise<boolean>;
  onCancel?: () => void;
  noConnections: boolean;
  /**
   * Why Save anyway is refused for the answer on screen (C2 review C2-01 / C2-02: a validation or security refusal of the
   * settings), or undefined. The strip hides the button for the same reason; the handler enforces it.
   */
  saveAnywayRefusal?: () => string | undefined;
  /** How the saved engine's draft was filled (`origin` on the saved events, C3): manual, paste, prefill or azure. */
  originOf?: (engine: ConnectionFormValues['engine']) => SaveOrigin;
  editConnection: Connection | undefined;
  connectionId: string;
  setConnectionId: (id: string) => void;
  track: TrackFunction;
};

export function useConnectionSave(options: Options) {
  const { form, tests, draft, saving, runGate, runTest, track } = options;
  const saveAuthRef = useRef<number | null>(null);
  const dismissedRef = useRef(false);
  const isEditMode = !!options.editConnection;
  const originOf = (engine: ConnectionFormValues['engine']): SaveOrigin => options.originOf?.(engine) ?? 'manual';

  /** A save whose draft moved on refuses and says so (silently once the dialog was dismissed). */
  const refuseStaleSave = () => {
    if (dismissedRef.current) return;
    tests.show(tests.snapshotOf(), { result: { success: false, variant: 'warning', message: CHANGED_WHILE_TESTING } });
  };

  /** Still authorized for `authorized`? Otherwise refuse. */
  const stillAuthorized = (authorized: number): boolean => {
    if (draft.isCurrent(authorized)) return true;
    refuseStaleSave();
    return false;
  };

  // The shared tail of Save (after its test passed) and Save anyway.
  const persist = async (values: ConnectionFormValues, authorized: number): Promise<boolean> => {
    if (!stillAuthorized(authorized)) return false;
    // S7 §3.2: a connected connection's named shares are replaced live first; the record stores the answer's set.
    const toSave = isEditMode ? await namedSharesForSave(values, options.editConnection) : values;
    if (!stillAuthorized(authorized)) return false;
    const passwordDirty = form.state.fieldMeta.password?.isDirty === true;
    // The probe dials with the typed SSH secrets; the saved record never carries them.
    const saved = await options.onSave(
      stripTransientSshSecrets(buildConnection(toSave, options.connectionId)),
      saveOptionsFor(values, passwordDirty),
    );
    if (saved) {
      options.onSaved();
      form.reset();
      // A new draft (the no-connections flow keeps the dialog open): nothing of the saved one, of any engine, returns.
      tests.clear();
      tests.bumpGeneration();
      options.setConnectionId(uuidv4());
      if (!options.noConnections && options.onCancel) options.onCancel();
    }
    return saved;
  };

  /** `form`'s onSubmit: validation already passed (the gate, then `handleSubmit`'s own). */
  const submit = async (value: ConnectionFormValues) => {
    const authorized = saveAuthRef.current ?? draft.capture();
    saveAuthRef.current = null;
    if (!draft.isCurrent(authorized)) return refuseStaleSave();
    const run = await runTest(normalizeValues(value), true, 'submit');
    // A failed test, or one voided while it ran (dismissal, an engine change): no save, even after a late success.
    if (!run.success || run.discarded) return;
    // The test answered for the values it dialed: when the form changed meanwhile, neither set may be saved.
    if (!draft.isCurrent(authorized) || !run.snapshot || !run.values || !tests.matchesLive(run.snapshot)) return refuseStaleSave();
    // The tested values including the run's own edit (the storage untick) — the unchanged revision proves they are on screen.
    const normalized = normalizeValues(run.values);
    // Read before persisting: a save that keeps the dialog open clears the drafts (and their origins).
    const origin = originOf(normalized.engine);
    if (await persist(normalized, authorized)) {
      track('feature:connection_saved', { engine: normalized.engine, limit_to_database: Boolean(normalized.limitToDatabase), is_edit: isEditMode, origin, funnel_version: FUNNEL_VERSION });
    }
  };

  // Both capture their authorization before the gate (their first await); they count as in flight once it passed.
  const saveForm = async () => {
    const authorized = draft.capture();
    if (!(await runGate())) return;
    const started = saving.begin();
    try {
      saveAuthRef.current = authorized;
      await form.handleSubmit();
    } finally {
      saveAuthRef.current = null;
      saving.end(started);
    }
  };

  // A failed test does not hard-block saving (the server may just be unreachable right now): the values on screen are
  // saved untested; analytics fire only after the save succeeds.
  const saveAnyway = async () => {
    // Not for a refusal of the settings themselves (a changed SSH host key, a target the tunnel cannot forward, a storage
    // resource ID naming another account): saving would only store what the app refuses.
    if (options.saveAnywayRefusal?.()) return;
    const authorized = draft.capture();
    if (!(await runGate())) return;
    const started = saving.begin();
    try {
      if (!draft.isCurrent(authorized)) return refuseStaleSave();
      const normalized = normalizeValues(form.state.values);
      const origin = originOf(normalized.engine);
      if (await persist(normalized, authorized)) {
        track('feature:connection_saved_untested', { engine: normalized.engine, limit_to_database: Boolean(normalized.limitToDatabase), origin, funnel_version: FUNNEL_VERSION });
      }
    } finally {
      saving.end(started);
    }
  };

  return { submit, saveForm, saveAnyway, markDismissed: () => { dismissedRef.current = true; } };
}
